import { applyResolvedTheme } from './applyTheme';
import type { ResolvedTheme } from './resolveTheme';

export interface ThemeTransitionOrigin {
  x: number;
  y: number;
}

export interface ApplyThemeOptions {
  /** Circular reveal when supported; instant otherwise */
  animate?: boolean;
  origin?: ThemeTransitionOrigin;
  /**
   * Runs immediately after the DOM flip. Inside a view transition this is the last
   * point where token-reading canvases (D3 charts) can redraw before the new
   * snapshot is captured, which keeps their work off the animation frames.
   */
  onApplied?: (info: { insideTransition: boolean }) => void;
}

type ViewTransitionResult = {
  ready: Promise<void>;
  finished: Promise<void>;
  skipTransition: () => void;
  updateCallbackDone: Promise<void>;
};

type DocumentWithViewTransition = Document & {
  startViewTransition?: (updateCallback: () => void) => ViewTransitionResult;
};

const THEME_TRANSITION_MS = 500;

const FADE_ANIMATION_OPTIONS = {
  duration: THEME_TRANSITION_MS,
  easing: 'ease-in-out',
  fill: 'forwards' as FillMode,
};

export function shouldAnimateThemeChange(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
  if (reducedMotion) {
    return false;
  }
  const doc = document as DocumentWithViewTransition;
  return typeof doc.startViewTransition === 'function';
}

/**
 * Google Chrome desktop janks clip-path on ::view-transition-new; use compositor-safe opacity fade.
 * Excludes Edge, Opera, Electron, and Cursor — they keep the circular reveal.
 */
export function prefersFadeThemeTransition(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent;
  if (/Edg\/|OPR\/|SamsungBrowser|Electron|Cursor\//i.test(ua)) {
    return false;
  }

  const nav = navigator as Navigator & {
    userAgentData?: { brands: Array<{ brand: string }> };
  };

  const brands = nav.userAgentData?.brands ?? [];
  if (brands.length > 0) {
    return brands.some((entry) => entry.brand === 'Google Chrome');
  }

  return /Chrome\//.test(ua) && !/Chromium\//.test(ua);
}

/** Furthest corner distance so the circle fully covers the viewport. */
export function getThemeRevealRadius(x: number, y: number): number {
  return Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y));
}

function runFadeTransition(transition: ViewTransitionResult): void {
  void transition.ready
    .then(() => {
      document.documentElement.animate(
        { opacity: [1, 0] },
        { ...FADE_ANIMATION_OPTIONS, pseudoElement: '::view-transition-old(root)' }
      );
      document.documentElement.animate(
        { opacity: [0, 1] },
        { ...FADE_ANIMATION_OPTIONS, pseudoElement: '::view-transition-new(root)' }
      );
    })
    .catch(() => {
      // Transition skipped or interrupted — theme already applied in callback.
    });
}

function runCircleRevealTransition(
  transition: ViewTransitionResult,
  x: number,
  y: number,
  endRadius: number
): void {
  void transition.ready
    .then(() => {
      document.documentElement.animate(
        {
          clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${endRadius}px at ${x}px ${y}px)`],
        },
        {
          duration: THEME_TRANSITION_MS,
          easing: 'ease-in-out',
          pseudoElement: '::view-transition-new(root)',
        }
      );
    })
    .catch(() => {
      // Transition skipped or interrupted — theme already applied in callback.
    });
}

export function applyResolvedThemeWithTransition(
  resolved: ResolvedTheme,
  options?: ApplyThemeOptions
): void {
  if (!options?.animate || !shouldAnimateThemeChange()) {
    applyResolvedTheme(resolved);
    options?.onApplied?.({ insideTransition: false });
    return;
  }

  const x = options.origin?.x ?? window.innerWidth / 2;
  const y = options.origin?.y ?? window.innerHeight / 2;
  const endRadius = getThemeRevealRadius(x, y);

  let transition: ViewTransitionResult;
  try {
    transition = (document as DocumentWithViewTransition).startViewTransition!(() => {
      applyResolvedTheme(resolved);
      options.onApplied?.({ insideTransition: true });
    });
  } catch {
    // Never leave the theme unapplied because the transition could not start.
    applyResolvedTheme(resolved);
    options.onApplied?.({ insideTransition: false });
    return;
  }

  if (prefersFadeThemeTransition()) {
    runFadeTransition(transition);
    return;
  }

  runCircleRevealTransition(transition, x, y, endRadius);
}
